import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { createFacility, updateFacility } from "service/admin/facilityService";
import { toast } from "react-toastify";

const CATEGORIES = [
  { value: "sports_facility", label: "Sports Facility" },
  { value: "gym", label: "Gym" },
  { value: "swimming_pool", label: "Swimming Pool" },
  { value: "cricket_academy", label: "Cricket Academy" },
  { value: "football_ground", label: "Football Ground" },
  { value: "tennis_court", label: "Tennis Court" },
  { value: "badminton_court", label: "Badminton Court" },
  { value: "basketball_court", label: "Basketball Court" },
  { value: "yoga_studio", label: "Yoga Studio" },
  { value: "other", label: "Other" },
];

const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
];

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  address: yup.string().required("Address is required"),
  platform_commission_pct: yup
    .number()
    .min(0)
    .max(100)
    .nullable()
    .typeError("Must be a number"),
});

const FacilityForm = ({ open, onClose, facility, onSuccess }) => {
  const isEdit = Boolean(facility);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: facility?.name || "",
      email: facility?.email || "",
      phone: facility?.phone || "",
      category: facility?.category || "",
      address: facility?.address || "",
      geo_lat: facility?.geo_lat || "",
      geo_long: facility?.geo_long || "",
      about: facility?.about || "",
      available_sports: facility?.available_sports || "",
      weekdays: facility?.weekdays || "",
      start_time: facility?.start_time || "",
      end_time: facility?.end_time || "",
      web_url: facility?.web_url || "",
      is_strikie_owned: facility?.is_strikie_owned || false,
      registration_status: facility?.registration_status || "pending",
      platform_commission_pct: facility?.platform_commission_pct ?? "",
      operating_hours_json: facility?.operating_hours
        ? JSON.stringify(facility.operating_hours, null, 2)
        : "",
      bank_account_holder: facility?.bank_details?.account_holder || "",
      bank_account_number: facility?.bank_details?.account_number || "",
      bank_ifsc: facility?.bank_details?.ifsc || "",
      bank_name: facility?.bank_details?.bank_name || "",
      bank_upi_id: facility?.bank_details?.upi_id || "",
      images: facility?.images ? facility.images.join(", ") : "",
      amenities: facility?.amenities
        ? Array.isArray(facility.amenities)
          ? facility.amenities.join(", ")
          : facility.amenities
        : "",
    },
    validationSchema,
    onSubmit: async (values) => {
      let operatingHours = undefined;
      if (values.operating_hours_json) {
        try {
          operatingHours = JSON.parse(values.operating_hours_json);
        } catch {
          toast.error("Operating Hours JSON is invalid.");
          return;
        }
      }

      const payload = {
        name: values.name,
        email: values.email || undefined,
        phone: values.phone || undefined,
        category: values.category || undefined,
        address: values.address,
        geo_lat: values.geo_lat || undefined,
        geo_long: values.geo_long || undefined,
        about: values.about || undefined,
        available_sports: values.available_sports || undefined,
        weekdays: values.weekdays || undefined,
        start_time: values.start_time || undefined,
        end_time: values.end_time || undefined,
        web_url: values.web_url || undefined,
        is_strikie_owned: values.is_strikie_owned,
        registration_status: values.registration_status,
        platform_commission_pct:
          values.platform_commission_pct !== ""
            ? Number(values.platform_commission_pct)
            : undefined,
        operating_hours: operatingHours,
        bank_details: {
          account_holder: values.bank_account_holder || undefined,
          account_number: values.bank_account_number || undefined,
          ifsc: values.bank_ifsc || undefined,
          bank_name: values.bank_name || undefined,
          upi_id: values.bank_upi_id || undefined,
        },
        images: values.images
          ? values.images.split(",").map((s) => s.trim()).filter(Boolean)
          : undefined,
        amenities: values.amenities
          ? values.amenities.split(",").map((s) => s.trim()).filter(Boolean)
          : undefined,
      };

      try {
        if (isEdit) {
          await updateFacility(facility.id, payload);
          toast.success("Facility updated successfully.");
        } else {
          await createFacility(payload);
          toast.success("Facility created successfully.");
        }
        onSuccess && onSuccess();
        onClose();
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            (isEdit ? "Failed to update facility." : "Failed to create facility.")
        );
      }
    },
  });

  const field = (name, label, props = {}) => (
    <TextField
      fullWidth
      size="small"
      name={name}
      label={label}
      color="secondary"
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
      {...props}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Facility" : "Add Facility"}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Info
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("name", "Name *")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("email", "Email")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("phone", "Phone")}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                name="category"
                label="Category"
                color="secondary"
                value={formik.values.category}
                onChange={formik.handleChange}
              >
                <MenuItem value="">None</MenuItem>
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              {field("address", "Address *")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("geo_lat", "Geo Latitude")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("geo_long", "Geo Longitude")}
            </Grid>
            <Grid item xs={12}>
              {field("about", "About", { multiline: true, rows: 3 })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("available_sports", "Available Sports")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("weekdays", "Weekdays")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("start_time", "Start Time", { placeholder: "HH:MM" })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("end_time", "End Time", { placeholder: "HH:MM" })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("web_url", "Web URL")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("platform_commission_pct", "Platform Commission %", {
                type: "number",
                inputProps: { min: 0, max: 100 },
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                name="registration_status"
                label="Registration Status"
                color="secondary"
                value={formik.values.registration_status}
                onChange={formik.handleChange}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    name="is_strikie_owned"
                    checked={formik.values.is_strikie_owned}
                    onChange={formik.handleChange}
                  />
                }
                label="Strikie Owned"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" gutterBottom>
                Operating Hours JSON
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {field("operating_hours_json", "Operating Hours JSON", {
                multiline: true,
                rows: 4,
                placeholder: '{"monday": {"open": "09:00", "close": "21:00"}}',
              })}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" gutterBottom>
                Bank Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("bank_account_holder", "Account Holder")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("bank_account_number", "Account Number")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("bank_ifsc", "IFSC Code")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("bank_name", "Bank Name")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("bank_upi_id", "UPI ID")}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" gutterBottom>
                Media & Amenities
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {field("images", "Image URLs (comma-separated)")}
            </Grid>
            <Grid item xs={12}>
              {field("amenities", "Amenities (comma-separated)")}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting
              ? isEdit
                ? "Saving..."
                : "Creating..."
              : isEdit
              ? "Save Changes"
              : "Create Facility"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FacilityForm;
